// SMPTE timecode arithmetic: seconds <-> HHMMSSFF.
//
// Drop-frame algorithm per SMPTE 12M:
//   At 29.97 fps, drop the first 2 frame numbers at the start of every minute
//   except minutes 0, 10, 20, 30, 40, 50.
//   At 59.94 fps, drop 4 frames per minute on the same schedule.
// Frame numbers are renumbered (skipped); actual frames are not removed.
/** Number of integer frames per nominal second (FF field range = 0..nominalFps-1). */
function nominalFps(frameRate) {
    return Math.round(frameRate.fps);
}
function isDropFrameRate(frameRate) {
    if (!frameRate.dropFrame)
        return false;
    const nf = nominalFps(frameRate);
    return nf === 30 || nf === 60;
}
function pad2(n) {
    return n.toString().padStart(2, '0');
}
/** Round seconds to a frame boundary (snap to nearest frame). */
export function snapToFrame(seconds, frameRate) {
    if (!Number.isFinite(seconds))
        throw new Error('snapToFrame: seconds must be finite');
    if (frameRate.fps <= 0)
        throw new Error('snapToFrame: fps must be > 0');
    const frames = Math.round(seconds * frameRate.fps);
    return frames / frameRate.fps;
}
/** Round seconds to a sample boundary at the given sample rate. */
export function roundToSample(seconds, sampleRate) {
    if (!Number.isFinite(seconds))
        throw new Error('roundToSample: seconds must be finite');
    if (sampleRate <= 0)
        throw new Error('roundToSample: sampleRate must be > 0');
    return Math.round(seconds * sampleRate) / sampleRate;
}
/**
 * Convert total absolute frame count to drop-frame H:M:S:F components.
 * Standard SMPTE drop-frame algorithm.
 */
function dfFramesToHMSF(totalFrames, nf, dropPerMin) {
    // Frames per 10-minute block: 10 * 60 * nf - 9 * dropPerMin (no drop at minute 0 of the block).
    const framesPer10Min = 10 * 60 * nf - 9 * dropPerMin;
    // Frames per minute (with drop): 60 * nf - dropPerMin.
    const framesPerMin = 60 * nf - dropPerMin;
    let frames = totalFrames;
    const d = Math.floor(frames / framesPer10Min);
    const m = frames % framesPer10Min;
    if (m > dropPerMin) {
        // Compensation: dropPerMin extra frames added to non-zero-minute counts within the 10-min block.
        frames = frames + dropPerMin * 9 * d + dropPerMin * Math.floor((m - dropPerMin) / framesPerMin);
    }
    else {
        frames = frames + dropPerMin * 9 * d;
    }
    const fField = frames % nf;
    const sField = Math.floor(frames / nf) % 60;
    const mField = Math.floor(frames / (nf * 60)) % 60;
    const hField = Math.floor(frames / (nf * 3600)) % 24;
    return { h: hField, m: mField, s: sField, f: fField };
}
/**
 * Inverse: drop-frame H:M:S:F components -> total absolute frame count.
 */
function dfHMSFToFrames(h, m, s, f, nf, dropPerMin) {
    const totalMinutes = 60 * h + m;
    const dropped = dropPerMin * (totalMinutes - Math.floor(totalMinutes / 10));
    return h * 3600 * nf + m * 60 * nf + s * nf + f - dropped;
}
/** Format seconds as SMPTE 8-digit HHMMSSFF (no separators). */
export function secondsToSmpte8(seconds, frameRate) {
    if (!Number.isFinite(seconds))
        throw new Error('secondsToSmpte8: seconds must be finite');
    if (seconds < 0)
        throw new Error('secondsToSmpte8: seconds must be >= 0');
    const nf = nominalFps(frameRate);
    let h;
    let m;
    let s;
    let f;
    if (isDropFrameRate(frameRate)) {
        // For DF, the timecode advances at nominal rate (30 or 60), but real time runs at 29.97/59.94.
        // totalFrames = round(seconds * actualFps).
        const totalFrames = Math.round(seconds * frameRate.fps);
        const dropPerMin = nf === 30 ? 2 : 4;
        const c = dfFramesToHMSF(totalFrames, nf, dropPerMin);
        h = c.h;
        m = c.m;
        s = c.s;
        f = c.f;
    }
    else {
        // NDF: timecode advances at nominal rate. For 23.976/24/25/29.97-NDF/30/50/59.94-NDF/60,
        // we treat fps as nominal for stamp purposes.
        const totalFrames = Math.round(seconds * frameRate.fps);
        f = totalFrames % nf;
        const totalSeconds = Math.floor(totalFrames / nf);
        s = totalSeconds % 60;
        m = Math.floor(totalSeconds / 60) % 60;
        h = Math.floor(totalSeconds / 3600);
    }
    if (h > 99)
        throw new Error(`secondsToSmpte8: HH out of 2-digit range: ${h}`);
    if (h > 23)
        throw new Error(`secondsToSmpte8: HH must be <= 23 (got ${h})`);
    return `${pad2(h)}${pad2(m)}${pad2(s)}${pad2(f)}`;
}
/** Format seconds as SMPTE display HH:MM:SS:FF (NDF) or HH:MM:SS;FF (DF). */
export function secondsToSmpteDisplay(seconds, frameRate) {
    const eight = secondsToSmpte8(seconds, frameRate);
    const hh = eight.slice(0, 2);
    const mm = eight.slice(2, 4);
    const ss = eight.slice(4, 6);
    const ff = eight.slice(6, 8);
    const lastSep = isDropFrameRate(frameRate) ? ';' : ':';
    return `${hh}:${mm}:${ss}${lastSep}${ff}`;
}
/** Parse SMPTE HHMMSSFF (with or without separators) back to seconds. */
export function smpte8ToSeconds(smpte, frameRate) {
    if (typeof smpte !== 'string')
        throw new Error('smpte8ToSeconds: input must be string');
    const cleaned = smpte.replace(/[^0-9]/g, '');
    if (cleaned.length !== 8) {
        throw new Error(`smpte8ToSeconds: expected 8 digits, got ${cleaned.length} ("${smpte}")`);
    }
    const h = Number.parseInt(cleaned.slice(0, 2), 10);
    const m = Number.parseInt(cleaned.slice(2, 4), 10);
    const s = Number.parseInt(cleaned.slice(4, 6), 10);
    const f = Number.parseInt(cleaned.slice(6, 8), 10);
    const nf = nominalFps(frameRate);
    if (h > 23)
        throw new Error(`smpte8ToSeconds: HH out of range: ${h}`);
    if (m > 59)
        throw new Error(`smpte8ToSeconds: MM out of range: ${m}`);
    if (s > 59)
        throw new Error(`smpte8ToSeconds: SS out of range: ${s}`);
    if (f >= nf)
        throw new Error(`smpte8ToSeconds: FF out of range for fps ${frameRate.fps}: ${f}`);
    if (isDropFrameRate(frameRate)) {
        const dropPerMin = nf === 30 ? 2 : 4;
        const totalFrames = dfHMSFToFrames(h, m, s, f, nf, dropPerMin);
        return totalFrames / frameRate.fps;
    }
    const totalFrames = h * 3600 * nf + m * 60 * nf + s * nf + f;
    return totalFrames / frameRate.fps;
}
/**
 * Format a duration in seconds as 8-digit HHMMSSFF for the DUR field.
 * Durations may not be wall-clock SMPTE (no DF compensation), they are
 * a straight count of hours/minutes/seconds/frames.
 */
export function secondsToDur(seconds, frameRate) {
    if (!Number.isFinite(seconds))
        throw new Error('secondsToDur: seconds must be finite');
    if (seconds < 0)
        throw new Error('secondsToDur: seconds must be >= 0');
    const nf = nominalFps(frameRate);
    const totalFrames = Math.round(seconds * frameRate.fps);
    const f = totalFrames % nf;
    const totalSeconds = Math.floor(totalFrames / nf);
    const s = totalSeconds % 60;
    const m = Math.floor(totalSeconds / 60) % 60;
    const h = Math.floor(totalSeconds / 3600);
    if (h > 99)
        throw new Error(`secondsToDur: HH exceeds 2 digits: ${h}`);
    return `${pad2(h)}${pad2(m)}${pad2(s)}${pad2(f)}`;
}
